/**
 * NL-010 manual seasonal planning scenarios.
 *
 * The NL-010 evidence engine (`./seasonality/engine.ts`) is evidence-gated by
 * construction: an enabled calculation is refused unless an approved source
 * record resolves. The seasonal evidence registry is intentionally empty and
 * stays that way until an owner approves a real source, so this module provides
 * the only scenario path that is usable today - one built entirely from the
 * visitor's own stated assumptions.
 *
 * The distinction is deliberate and must not be blurred: nothing here claims
 * external market evidence. Every number a visitor sees traces back to a
 * percentage that same visitor typed, labelled as their own assumption.
 *
 * Two properties are structural rather than conventional:
 *
 * 1. **Seasonality is applied exactly once, to demand.** `appliedTo` is the
 *    single literal `"monthly-visitors"`, and a caller that has already applied
 *    a seasonal multiplier upstream is refused rather than compounded.
 * 2. **Seasonality never moves revenue on its own.** `revenueMultiplierApplied`
 *    is the single literal `false`. Revenue changes only because demand changed,
 *    through the existing projection engine.
 *
 * The module is pure: no clock, no I/O, no randomness, no state mutation.
 */
import {
  DECIMAL_ROUNDING_CONTRACT_VERSION,
  roundDecimalQuantityHalfUp,
} from "@/lib/calculator-contracts/decimal";
import {
  SEASONAL_PLANNING_SCENARIOS,
  type SeasonalPlanningScenario,
} from "@/lib/funnel-planner/schema";

export const SEASONAL_SCENARIO_METHOD_VERSION = "funnel-manual-seasonal-scenarios-v1";

/** Makes the assumption's ownership explicit everywhere it is reported. */
export const SEASONAL_ASSUMPTION_LABEL = "visitor-manual-assumption" as const;

/** Matches the `SeasonalDemandSchema` percent bounds. */
export const SEASONAL_PERCENT_MIN = -90;
export const SEASONAL_PERCENT_MAX = 300;

export type SeasonalScenarioBlocker =
  | "invalid-baseline-visitors"
  | "missing-assumption"
  | "assumption-out-of-range"
  | "assumption-order-invalid"
  | "seasonal-multiplier-already-applied"
  | "revenue-multiplier-not-supported";

/** Any supplied property is an attempted second application of seasonality. */
export type SeasonalExistingMultipliers = {
  readonly seasonal?: unknown;
  readonly revenue?: unknown;
};

export type SeasonalScenarioAssumptions = {
  readonly lowerPercent: number | null;
  readonly expectedPercent: number | null;
  readonly upperPercent: number | null;
};

export type ManualSeasonalScenarioInput = {
  readonly enabled: boolean;
  readonly baselineMonthlyVisitors: number;
  readonly assumptions: SeasonalScenarioAssumptions;
  readonly existingMultipliers?: SeasonalExistingMultipliers;
};

export type SeasonalScenarioOutput = {
  readonly scenario: SeasonalPlanningScenario;
  readonly percent: number;
  /** Fixed two-decimal string, matching the NL-010 multiplier format. */
  readonly multiplier: string;
  readonly monthlyVisitors: number;
};

export type ManualSeasonalScenarioResult = {
  readonly methodVersion: typeof SEASONAL_SCENARIO_METHOD_VERSION;
  /** The pinned rounding contract the visitor figures are rounded through. */
  readonly decimalRoundingContractVersion: typeof DECIMAL_ROUNDING_CONTRACT_VERSION;
  readonly readiness: "disabled" | "ready" | "blocked";
  readonly blockers: readonly SeasonalScenarioBlocker[];
  readonly assumptionLabel: typeof SEASONAL_ASSUMPTION_LABEL | null;
  /** The single point seasonality touches. */
  readonly appliedTo: "monthly-visitors";
  /** Structurally pinned: seasonality never produces its own revenue uplift. */
  readonly revenueMultiplierApplied: false;
  readonly scenarios: readonly SeasonalScenarioOutput[];
};

const DISABLED_MULTIPLIER = "1.00";

function addUnique(blockers: SeasonalScenarioBlocker[], blocker: SeasonalScenarioBlocker): void {
  if (!blockers.includes(blocker)) blockers.push(blocker);
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function formatHundredths(hundredths: number): string {
  const whole = Math.floor(hundredths / 100);
  const fraction = hundredths % 100;

  return `${whole}.${String(fraction).padStart(2, "0")}`;
}

/**
 * Decomposes a finite number into the exact `numerator / 10 ** scale` pair
 * implied by its shortest round-trip decimal representation.
 *
 * This is what makes the multiplier exact. `String(0.495)` is `"0.495"`, so the
 * value rounded is the value the visitor actually entered, with no intermediate
 * binary approximation. Returns `null` if the number cannot be represented
 * exactly as a safe integer numerator, so callers fail closed rather than
 * silently rounding something else.
 */
function decomposeDecimal(value: number): { readonly numerator: number; readonly scale: number } | null {
  if (!Number.isFinite(value)) return null;

  const [mantissa, exponentText] = String(value).split(/[eE]/);
  const exponent = exponentText ? Number(exponentText) : 0;
  const negative = mantissa.startsWith("-");
  const unsigned = negative ? mantissa.slice(1) : mantissa;
  const [whole, fraction = ""] = unsigned.split(".");

  let numerator = Number(`${whole}${fraction}`);
  let scale = fraction.length - exponent;

  if (!Number.isFinite(numerator)) return null;

  // A negative scale means the exponent shifted the value left; fold it in so
  // the pair is always (integer numerator, non-negative scale).
  if (scale < 0) {
    numerator *= 10 ** -scale;
    scale = 0;
  }

  // Scale 13 is the exact safe bound, not a round number: the caller's largest
  // accepted percent is 300, so the widest intermediate is
  // `100 * 10**scale + 300 * 10**scale = 4e2 * 10**scale`, which stays under
  // Number.MAX_SAFE_INTEGER (~9.007e15) up to and including scale 13. Beyond
  // that the integer arithmetic would silently lose precision, so it fails
  // closed instead.
  if (!Number.isSafeInteger(numerator) || scale > 13) return null;

  return { numerator: negative ? -numerator : numerator, scale };
}

/**
 * The exact decimal string for `100 + percent`, with no floating-point step.
 *
 * `percent` is bounded to [-90, 300] by the caller, so the sum is always
 * positive and no sign handling is needed here.
 */
function hundredPlusPercentDecimal(percent: number): string | null {
  const parts = decomposeDecimal(percent);
  if (!parts) return null;

  const factor = 10 ** parts.scale;
  const total = 100 * factor + parts.numerator;

  if (!Number.isSafeInteger(total) || total < 0) return null;
  if (parts.scale === 0) return String(total);

  const whole = Math.floor(total / factor);
  const fraction = total - whole * factor;

  return `${whole}.${String(fraction).padStart(parts.scale, "0")}`;
}

/**
 * Converts a percentage into an exact two-decimal multiplier: -15 becomes
 * "0.85", 20 becomes "1.20", 12.5 becomes "1.13" (half-up at the second
 * decimal).
 *
 * The multiplier in hundredths is exactly `half-up(100 + percent)`, and it is
 * computed in **one** rounding step through the pinned `nl014-decimal-v1`
 * contract.
 *
 * This previously rounded twice — `Math.round(percent * 100)` snapped the
 * percent to two decimals before the multiplier's own half-up — which produced
 * a wrong multiplier for 1,928 of the three-decimal percents in the accepted
 * range. `percent = 0.495` returned "1.01" instead of "1.00", and
 * `percent = -89.501` returned "0.11" instead of "0.10", a 10% error on the
 * planned visitor figure. Schema validation accepts those values (it bounds the
 * range but not the number of decimals), and a restored draft or share URL
 * bypasses the input's `step` entirely, so the extra decimals were reachable.
 *
 * Returns `null` when the percent cannot be represented exactly, so the caller
 * blocks instead of emitting a number derived from an approximation.
 */
function percentToMultiplierHundredths(percent: number): number | null {
  const value = hundredPlusPercentDecimal(percent);
  if (value === null) return null;

  const rounded = roundDecimalQuantityHalfUp({
    quantity: { value, unit: "seasonal-multiplier-hundredths" },
    outputScale: 0,
  });

  if (rounded.readiness !== "ready" || !rounded.roundedQuantity) return null;

  const hundredths = Number(rounded.roundedQuantity.value);

  return Number.isSafeInteger(hundredths) && hundredths >= 0 ? hundredths : null;
}

/** Renders a non-negative integer count of hundredths as a plain decimal string. */
function hundredthsToDecimalString(hundredths: number): string {
  const whole = Math.floor(hundredths / 100);
  const fraction = hundredths % 100;

  return `${whole}.${String(fraction).padStart(2, "0")}`;
}

/**
 * Rounds `baselineMonthlyVisitors * multiplierHundredths / 100` to whole
 * visitors through the pinned `nl014-decimal-v1` half-up contract. The product
 * is an exact integer for the bounded visitor counts this planner accepts, and
 * the half-up rounding rule is now owned by the contract rather than a local
 * float `Math.floor(n / d + 0.5)`.
 */
function roundedMonthlyVisitors(baselineMonthlyVisitors: number, multiplierHundredths: number): number {
  const productHundredths = baselineMonthlyVisitors * multiplierHundredths;
  const rounded = roundDecimalQuantityHalfUp({
    quantity: { value: hundredthsToDecimalString(productHundredths), unit: "monthly-visitors" },
    outputScale: 0,
  });

  if (rounded.readiness !== "ready" || !rounded.roundedQuantity) {
    // Unreachable for a validated non-negative integer baseline; fail closed to
    // the untouched baseline rather than emitting a wrong number.
    return baselineMonthlyVisitors;
  }

  return Number(rounded.roundedQuantity.value);
}

function inRange(percent: number): boolean {
  return percent >= SEASONAL_PERCENT_MIN && percent <= SEASONAL_PERCENT_MAX;
}

function result(
  readiness: ManualSeasonalScenarioResult["readiness"],
  blockers: readonly SeasonalScenarioBlocker[],
  assumptionLabel: ManualSeasonalScenarioResult["assumptionLabel"],
  scenarios: readonly SeasonalScenarioOutput[],
): ManualSeasonalScenarioResult {
  return {
    methodVersion: SEASONAL_SCENARIO_METHOD_VERSION,
    decimalRoundingContractVersion: DECIMAL_ROUNDING_CONTRACT_VERSION,
    readiness,
    blockers,
    assumptionLabel,
    appliedTo: "monthly-visitors",
    revenueMultiplierApplied: false,
    scenarios,
  };
}

/** The neutral baseline every scenario collapses to while seasonality is off. */
function disabledScenarios(baselineMonthlyVisitors: number): readonly SeasonalScenarioOutput[] {
  return SEASONAL_PLANNING_SCENARIOS.map((scenario) => ({
    scenario,
    percent: 0,
    multiplier: DISABLED_MULTIPLIER,
    monthlyVisitors: baselineMonthlyVisitors,
  }));
}

function inspectExistingMultipliers(
  existingMultipliers: SeasonalExistingMultipliers | undefined,
  blockers: SeasonalScenarioBlocker[],
): void {
  if (!existingMultipliers) return;

  if (Object.prototype.hasOwnProperty.call(existingMultipliers, "seasonal")) {
    addUnique(blockers, "seasonal-multiplier-already-applied");
  }
  if (Object.prototype.hasOwnProperty.call(existingMultipliers, "revenue")) {
    addUnique(blockers, "revenue-multiplier-not-supported");
  }
}

/**
 * Produces lower / expected / upper demand scenarios from visitor-stated
 * percentages.
 *
 * Fails closed: any missing, out-of-range, or out-of-order assumption blocks the
 * whole set rather than returning a partially adjusted plan, and a disabled
 * calculation returns the untouched baseline at multiplier "1.00".
 */
export function calculateManualSeasonalScenarios(
  input: ManualSeasonalScenarioInput,
): ManualSeasonalScenarioResult {
  const blockers: SeasonalScenarioBlocker[] = [];
  const { baselineMonthlyVisitors, assumptions } = input;

  const baselineValid =
    isFiniteNumber(baselineMonthlyVisitors) &&
    Number.isInteger(baselineMonthlyVisitors) &&
    baselineMonthlyVisitors >= 0;

  if (!baselineValid) addUnique(blockers, "invalid-baseline-visitors");

  if (input.enabled !== true) {
    return blockers.length === 0
      ? result("disabled", [], null, disabledScenarios(baselineMonthlyVisitors))
      : result("blocked", blockers, null, []);
  }

  inspectExistingMultipliers(input.existingMultipliers, blockers);

  const percents = [
    assumptions.lowerPercent,
    assumptions.expectedPercent,
    assumptions.upperPercent,
  ];

  if (percents.some((percent) => percent === null || percent === undefined)) {
    addUnique(blockers, "missing-assumption");
  } else if (!percents.every((percent) => isFiniteNumber(percent) && inRange(percent))) {
    addUnique(blockers, "assumption-out-of-range");
  } else {
    const [lower, expected, upper] = percents as [number, number, number];
    if (!(lower <= expected && expected <= upper)) {
      addUnique(blockers, "assumption-order-invalid");
    }
  }

  if (blockers.length > 0) return result("blocked", blockers, SEASONAL_ASSUMPTION_LABEL, []);

  const [lowerPercent, expectedPercent, upperPercent] = percents as [number, number, number];
  const byScenario: Readonly<Record<SeasonalPlanningScenario, number>> = {
    lower: lowerPercent,
    expected: expectedPercent,
    upper: upperPercent,
  };

  const scenarios: SeasonalScenarioOutput[] = [];

  for (const scenario of SEASONAL_PLANNING_SCENARIOS) {
    const percent = byScenario[scenario];
    const multiplierHundredths = percentToMultiplierHundredths(percent);

    // A percent that cannot be converted exactly blocks the whole result rather
    // than contributing an approximated scenario alongside exact ones.
    if (multiplierHundredths === null) {
      addUnique(blockers, "assumption-out-of-range");
      return result("blocked", blockers, SEASONAL_ASSUMPTION_LABEL, []);
    }

    scenarios.push({
      scenario,
      percent,
      multiplier: formatHundredths(multiplierHundredths),
      monthlyVisitors: roundedMonthlyVisitors(baselineMonthlyVisitors, multiplierHundredths),
    });
  }

  return result("ready", [], SEASONAL_ASSUMPTION_LABEL, scenarios);
}

/** Convenience lookup used by the projection UI and the explanation builder. */
export function findScenario(
  result_: ManualSeasonalScenarioResult,
  scenario: SeasonalPlanningScenario,
): SeasonalScenarioOutput | null {
  return result_.scenarios.find((entry) => entry.scenario === scenario) ?? null;
}
